import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';

const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(Number(value) || 0);
const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  const decimals = numericValue >= 1 ? 2 : 4;
  return `$${numericValue.toFixed(decimals)}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString('fr-FR');
};

const buildQueryString = (params) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });

  return query.toString();
};

const AITotalsCard = ({ label, value, helper, formatter = formatNumber }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{formatter(value)}</div>
      {helper ? (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helper}</div>
      ) : null}
    </div>
  );
};

const AIUsageTable = ({ title, columns, rows, emptyLabel, compact = false }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:text-gray-100">
        {title}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${compact ? 'px-3 py-1.5' : 'px-4 py-2'} text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={`${compact ? 'px-3 py-3 text-xs' : 'px-4 py-4 text-sm'} text-gray-500 dark:text-gray-400`}
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id || `${title}-${index}`}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${compact ? 'px-3 py-2 text-xs leading-tight' : 'px-4 py-3 text-sm'} text-gray-700 dark:text-gray-200 align-top`}
                    >
                      {column.render ? column.render(row) : row[column.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getPromptLabel = (row) => {
  if (row.promptMode === 'custom') {
    return row.customPrompt ? `${row.promptMode} · ${row.customPrompt.slice(0, 60)}` : row.promptMode;
  }

  const versionPart = row.promptVersion ? `@${row.promptVersion}` : '';
  return `${row.promptSlug || 'default'}${versionPart}`;
};

const getCompactContextLabel = (row) => {
  const questionCount = formatNumber(row.context?.questionPairsCount);
  const campaignCount = formatNumber(row.context?.campaignCount);
  const rawCount = formatNumber(row.context?.rawResponseCount ?? row.context?.responseCount);
  const uniqueCount = formatNumber(row.context?.dedupedResponseCount ?? row.context?.responseCount);
  const modelCount = formatNumber(row.context?.modelWeightedResponseCount ?? row.context?.responseCount);
  const keepRate = Math.round(Number(row.context?.responseRetentionRate ?? 1) * 100);

  return `Q ${questionCount} · C ${campaignCount} · B ${rawCount} · U ${uniqueCount} · M ${modelCount} · K ${keepRate}%`;
};

function StatisticsAIUsageTab({ startDate, endDate, fetchWithAuth, apiBaseUrl }) {
  const selectStyles = useSelectStyles();
  const [summary, setSummary] = useState({
    totals: { requests: 0, cachedRequests: 0, llmRequests: 0, customPromptRequests: 0, inputTokens: 0, outputTokens: 0, estPrice: null },
    byClient: [],
    byUser: [],
    byEndpoint: []
  });
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    clientId: '',
    module: '',
    endpoint: '',
    promptMode: '',
    username: ''
  });

  const onFilterChange = (key, value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const commonQuery = buildQueryString({
          startDate,
          endDate,
          clientId: filters.clientId,
          module: filters.module,
          endpoint: filters.endpoint,
          promptMode: filters.promptMode,
          username: filters.username
        });

        const logsQuery = buildQueryString({
          startDate,
          endDate,
          clientId: filters.clientId,
          module: filters.module,
          endpoint: filters.endpoint,
          promptMode: filters.promptMode,
          username: filters.username,
          page: pagination.page,
          pageSize: pagination.pageSize
        });

        const [summaryResponse, logsResponse] = await Promise.all([
          fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/ai-usage/summary?${commonQuery}`),
          fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/ai-usage/logs?${logsQuery}`)
        ]);

        if (!summaryResponse.ok || !logsResponse.ok) {
          throw new Error('Failed to load AI usage data');
        }

        const [summaryJson, logsJson] = await Promise.all([
          summaryResponse.json(),
          logsResponse.json()
        ]);

        setSummary(summaryJson.data || {
          totals: { requests: 0, cachedRequests: 0, llmRequests: 0, customPromptRequests: 0, inputTokens: 0, outputTokens: 0, estPrice: null },
          byClient: [],
          byUser: [],
          byEndpoint: []
        });
        setLogs(logsJson.data || []);
        setPagination((current) => ({
          ...current,
          total: logsJson.metadata?.total || 0,
          totalPages: logsJson.metadata?.totalPages || 1
        }));
      } catch (loadError) {
        setError('Failed to load AI usage data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [startDate, endDate, filters, pagination.page, pagination.pageSize, fetchWithAuth, apiBaseUrl]);

  const clientOptions = useMemo(() => {
    return summary.byClient.map((item) => ({
      value: item.clientId || '',
      label: item.clientName || item.clientId || 'Unknown client'
    }));
  }, [summary.byClient]);

  const moduleOptions = useMemo(() => {
    return Array.from(new Set(summary.byEndpoint.map((item) => item.moduleName).filter(Boolean)));
  }, [summary.byEndpoint]);

  const endpointOptions = useMemo(() => {
    return Array.from(new Set(summary.byEndpoint.map((item) => item.endpointKey).filter(Boolean)));
  }, [summary.byEndpoint]);

  const userOptions = useMemo(() => {
    return summary.byUser.map((item) => ({
      value: item.username || '',
      label: item.username || 'Unknown user'
    }));
  }, [summary.byUser]);

  const selectedClientOption = useMemo(() => {
    return clientOptions.find((option) => option.value === filters.clientId) || null;
  }, [clientOptions, filters.clientId]);

  const selectedUserOption = useMemo(() => {
    return userOptions.find((option) => option.value === filters.username) || null;
  }, [userOptions, filters.username]);

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
        Les filtres globaux Client et Outil ne s'appliquent pas ici.
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Client</label>
          <Select
            isClearable
            isSearchable
            value={selectedClientOption}
            onChange={(option) => onFilterChange('clientId', option?.value || '')}
            options={clientOptions}
            placeholder="Tous les clients"
            classNamePrefix="react-select"
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Module</label>
          <select
            value={filters.module}
            onChange={(event) => onFilterChange('module', event.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Tous</option>
            {moduleOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Endpoint</label>
          <select
            value={filters.endpoint}
            onChange={(event) => onFilterChange('endpoint', event.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Tous</option>
            {endpointOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Prompt mode</label>
          <select
            value={filters.promptMode}
            onChange={(event) => onFilterChange('promptMode', event.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Tous</option>
            <option value="default">default</option>
            <option value="custom">custom</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Utilisateur</label>
          <Select
            isClearable
            isSearchable
            value={selectedUserOption}
            onChange={(option) => onFilterChange('username', option?.value || '')}
            options={userOptions}
            placeholder="Tous les utilisateurs"
            classNamePrefix="react-select"
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-md bg-red-100 px-4 py-3 text-sm text-red-700 dark:bg-red-900 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <AITotalsCard label="Requêtes" value={summary.totals.requests} helper={`${formatNumber(summary.totals.llmRequests)} appels LLM`} />
            <AITotalsCard label="Input tokens" value={summary.totals.inputTokens} helper={`${formatNumber(summary.totals.cachedRequests)} lectures cache`} />
            <AITotalsCard label="Output tokens" value={summary.totals.outputTokens} />
            <AITotalsCard
              label="Prompts custom"
              value={summary.totals.customPromptRequests}
              helper=""
            />
            <AITotalsCard label="Coût estimé" value={summary.totals.estPrice} helper="USD" formatter={formatPrice} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <AIUsageTable
              title="Top clients"
              emptyLabel="Aucune donnée"
              columns={[
                { key: 'clientName', label: 'Client' },
                { key: 'requests', label: 'Req.' },
                { key: 'inputTokens', label: 'Input', render: (row) => formatNumber(row.inputTokens) },
                { key: 'outputTokens', label: 'Output', render: (row) => formatNumber(row.outputTokens) },
                { key: 'estPrice', label: 'Cost', render: (row) => formatPrice(row.estPrice) }
              ]}
              rows={summary.byClient}
            />
            <AIUsageTable
              title="Top users"
              emptyLabel="Aucune donnée"
              columns={[
                { key: 'username', label: 'Utilisateur' },
                { key: 'requests', label: 'Req.' },
                { key: 'inputTokens', label: 'Input', render: (row) => formatNumber(row.inputTokens) },
                { key: 'outputTokens', label: 'Output', render: (row) => formatNumber(row.outputTokens) },
                { key: 'estPrice', label: 'Cost', render: (row) => formatPrice(row.estPrice) }
              ]}
              rows={summary.byUser}
            />
            <AIUsageTable
              title="Top endpoints"
              emptyLabel="Aucune donnée"
              columns={[
                {
                  key: 'endpointKey',
                  label: 'Endpoint',
                  render: (row) => (
                    <div>
                      <div className="font-medium">{row.endpointKey}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.moduleName}</div>
                    </div>
                  )
                },
                { key: 'requests', label: 'Req.' },
                { key: 'inputTokens', label: 'Input', render: (row) => formatNumber(row.inputTokens) },
                { key: 'outputTokens', label: 'Output', render: (row) => formatNumber(row.outputTokens) },
                { key: 'estPrice', label: 'Cost', render: (row) => formatPrice(row.estPrice) }
              ]}
              rows={summary.byEndpoint}
            />
          </div>

          <AIUsageTable
            title="Logs détaillés"
            emptyLabel="Aucun log trouvé"
            compact={true}
            columns={[
              {
                key: 'createdAt',
                label: 'Date',
                render: (row) => (
                  <div className="whitespace-nowrap">
                    {formatDateTime(row.createdAt)}
                  </div>
                )
              },
              { key: 'username', label: 'Utilisateur' },
              {
                key: 'clientName',
                label: 'Client',
                render: (row) => row.clientName || row.clientId || '—'
              },
              {
                key: 'endpointKey',
                label: 'Feature',
                render: (row) => (
                  <div className="max-w-[180px]">
                    <div className="font-medium leading-tight">{row.endpointKey}</div>
                    <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">{row.moduleName}</div>
                  </div>
                )
              },
              {
                key: 'promptMode',
                label: 'Prompt',
                render: (row) => (
                  <div className="max-w-[220px]">
                    <div className="font-medium leading-tight">{row.promptMode}</div>
                    <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">{getPromptLabel(row)}</div>
                  </div>
                )
              },
              {
                key: 'tokenUsage',
                label: 'Tokens',
                render: (row) => (
                  <div className="whitespace-nowrap text-[11px]">
                    In {formatNumber(row.tokenUsage?.input_tokens ?? row.tokenUsage?.estimated_input_tokens)}
                    <span className="mx-1 text-gray-400">/</span>
                    Out {formatNumber(row.tokenUsage?.output_tokens ?? row.tokenUsage?.estimated_output_tokens)}
                  </div>
                )
              },
              {
                key: 'estPrice',
                label: 'Cost',
                render: (row) => formatPrice(row.estPrice)
              },
              {
                key: 'responseMeta',
                label: 'Statut',
                render: (row) => (
                  <div className="whitespace-nowrap text-[11px]">
                    <span className="font-medium">{row.responseMeta?.status || '—'}</span>
                    <span className="mx-1 text-gray-400">·</span>
                    <span className="text-gray-500 dark:text-gray-400">cache {row.responseMeta?.cached ? 'yes' : 'no'}</span>
                  </div>
                )
              },
              {
                key: 'context',
                label: 'Contexte',
                render: (row) => (
                  <div className="max-w-[250px] text-[11px] leading-tight text-gray-600 dark:text-gray-300">
                    {getCompactContextLabel(row)}
                  </div>
                )
              }
            ]}
            rows={logs}
          />

          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
            <div>
              Page {pagination.page} / {pagination.totalPages} · {formatNumber(pagination.total)} logs
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPagination((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
                disabled={pagination.page <= 1}
                className="rounded-md border border-gray-300 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700"
              >
                Précédent
              </button>
              <button
                type="button"
                onClick={() => setPagination((current) => ({ ...current, page: Math.min(current.totalPages, current.page + 1) }))}
                disabled={pagination.page >= pagination.totalPages}
                className="rounded-md border border-gray-300 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StatisticsAIUsageTab;
